// Phase 10B "AutoMind 2050" — SMS automation controls.
// PATCHes /api/dealers/{dealerId}/sms-automation. The component owns the
// "draft" copy of settings; it only talks to the API on Save and surfaces
// a diff vs. props so the Save button stays accurate.
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

import { DataCard } from '../../components/ui/data-card';
import { StatusPill } from '../../components/ui/status-pill';
import { smsFollowupsApi } from '../../api';
import { TRIGGER_FIELDS } from './constants';

const DEFAULT_TRIGGERS = {
  missed_call: false,
  high_intent_no_booking: false,
  requested_human_backup: false,
  appointment_confirmation: false,
};

function normalize(automation) {
  const a = automation || {};
  return {
    enabled: !!a.enabled,
    allow_after_hours: !!a.allow_after_hours,
    send_delay_minutes:
      typeof a.send_delay_minutes === 'number' ? a.send_delay_minutes : 0,
    triggers: { ...DEFAULT_TRIGGERS, ...(a.triggers || {}) },
  };
}

function diffPatch(draft, original) {
  const patch = {};
  if (draft.enabled !== original.enabled) patch.enabled = draft.enabled;
  if (draft.allow_after_hours !== original.allow_after_hours) {
    patch.allow_after_hours = draft.allow_after_hours;
  }
  if (draft.send_delay_minutes !== original.send_delay_minutes) {
    patch.send_delay_minutes = draft.send_delay_minutes;
  }
  const triggerDiff = {};
  for (const k of Object.keys(DEFAULT_TRIGGERS)) {
    if (!!draft.triggers[k] !== !!original.triggers[k]) {
      triggerDiff[k] = !!draft.triggers[k];
    }
  }
  if (Object.keys(triggerDiff).length > 0) patch.triggers = triggerDiff;
  return patch;
}

// Inverted-pill toggle: ink-900 (light) / ink-50 (dark) when on,
// ink-200 / ink-800 when off. Knob inside.
function Toggle({ checked, onChange, disabled, ariaLabel }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      className={
        'relative inline-flex h-5 w-9 items-center rounded-full ' +
        'transition-colors focus-2050 disabled:opacity-50 ' +
        'disabled:cursor-not-allowed ' +
        (checked
          ? 'bg-ink-900 dark:bg-ink-50'
          : 'bg-ink-200 dark:bg-ink-800')
      }
    >
      <span
        className={
          'inline-block h-4 w-4 transform rounded-full shadow-sm transition-transform ' +
          (checked
            ? 'translate-x-4 bg-white dark:bg-ink-900'
            : 'translate-x-0.5 bg-white dark:bg-ink-50')
        }
      />
    </button>
  );
}

export default function AutomationControls({
  dealerId,
  sms_automation,
  onUpdated,
  readOnly = false,
  loading = false,
  compact = false,
}) {
  const original = useMemo(() => normalize(sms_automation), [sms_automation]);
  const [draft, setDraft] = useState(original);
  const [saving, setSaving] = useState(false);

  // When the parent re-fetches and passes a new section, reset the draft.
  useEffect(() => {
    setDraft(original);
  }, [original]);

  const patch = useMemo(() => diffPatch(draft, original), [draft, original]);
  const dirty = Object.keys(patch).length > 0;

  const handleSave = async () => {
    if (!dealerId || !dirty || saving) return;
    setSaving(true);
    try {
      await smsFollowupsApi.updateAutomation(dealerId, patch);
      toast.success('Automation updated');
      if (onUpdated) await onUpdated();
    } catch (err) {
      toast.error(err?.message || 'Failed to update automation');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => setDraft(original);

  const setTrigger = (key, value) =>
    setDraft((d) => ({ ...d, triggers: { ...d.triggers, [key]: value } }));

  const disabled = readOnly || saving;

  return (
    <DataCard
      title={compact ? 'Automation' : 'Automation settings'}
      description={
        compact
          ? 'Quick toggles for the common triggers.'
          : 'Choose which triggers send an SMS, and how soon.'
      }
      loading={loading}
      actions={
        <StatusPill
          tone={draft.enabled ? 'success' : 'neutral'}
          label={draft.enabled ? 'Active' : 'Disabled'}
          dot
        />
      }
    >
      <div className="space-y-6">
        {readOnly && (
          <div className="text-xs text-ink-500">
            Read only — ask your manager to change settings.
          </div>
        )}

        {/* Master toggle */}
        <div className="flex items-start justify-between gap-6 pb-4 border-b border-ink-200/60 dark:border-ink-800/60">
          <div className="min-w-0">
            <div className="text-sm font-medium text-ink-900 dark:text-ink-50">
              Enable SMS automation
            </div>
            <div className="mt-0.5 text-xs text-ink-500">
              Turning this off blocks every trigger below regardless of their state.
            </div>
          </div>
          <Toggle
            checked={draft.enabled}
            onChange={(v) => setDraft((d) => ({ ...d, enabled: v }))}
            disabled={disabled}
            ariaLabel="Enable SMS automation"
          />
        </div>

        {/* Send delay + after-hours — hidden in compact (Overview) view */}
        {!compact && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label
                htmlFor="sms-send-delay"
                className="text-sm font-medium text-ink-900 dark:text-ink-50"
              >
                Send delay
              </label>
              <div className="mt-0.5 text-xs text-ink-500">
                Wait N min after a call before sending.
              </div>
              <div className="mt-3 flex items-center gap-3">
                <input
                  id="sms-send-delay"
                  type="range"
                  min={0}
                  max={15}
                  step={1}
                  value={draft.send_delay_minutes}
                  onChange={(e) =>
                    setDraft((d) => ({
                      ...d,
                      send_delay_minutes: Number(e.target.value),
                    }))
                  }
                  disabled={disabled}
                  className="flex-1 accent-ink-900 dark:accent-ink-50"
                />
                <input
                  type="number"
                  min={0}
                  max={15}
                  value={draft.send_delay_minutes}
                  onChange={(e) => {
                    const n = Math.max(0, Math.min(15, Number(e.target.value) || 0));
                    setDraft((d) => ({ ...d, send_delay_minutes: n }));
                  }}
                  disabled={disabled}
                  className="h-9 w-16 rounded-md border border-ink-200 dark:border-ink-800 bg-white/70 dark:bg-ink-900/40 px-2 text-sm nums focus-2050"
                />
                <span className="text-xs text-ink-500">min</span>
              </div>
            </div>

            <div>
              <div className="text-sm font-medium text-ink-900 dark:text-ink-50">
                Quiet hours
              </div>
              <div className="mt-0.5 text-xs text-ink-500">
                Default behaviour holds messages overnight.
              </div>
              <label className="mt-3 flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={draft.allow_after_hours}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, allow_after_hours: e.target.checked }))
                  }
                  disabled={disabled}
                  className="h-4 w-4 rounded border-ink-300 accent-ink-900 dark:accent-ink-50"
                />
                <span className="text-ink-900 dark:text-ink-50">
                  Allow after-hours sends
                </span>
              </label>
            </div>
          </div>
        )}

        {/* Triggers */}
        <div>
          <div className="text-[10px] uppercase tracking-[0.16em] text-ink-500 mb-3">
            Triggers
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {TRIGGER_FIELDS.map((t) => (
              <div
                key={t.key}
                className="flex items-start justify-between gap-3 rounded-xl border border-ink-200/70 dark:border-ink-800/70 bg-white/40 dark:bg-ink-900/30 px-4 py-3"
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium text-ink-900 dark:text-ink-50">
                    {t.label}
                  </div>
                  <div className="mt-0.5 text-xs text-ink-500">{t.helper}</div>
                </div>
                <Toggle
                  checked={!!draft.triggers[t.key]}
                  onChange={(v) => setTrigger(t.key, v)}
                  disabled={disabled || !draft.enabled}
                  ariaLabel={`Enable ${t.label}`}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Save row */}
        {!readOnly && (
          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              className="btn-2050 btn-ghost"
              onClick={handleCancel}
              disabled={!dirty || saving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-2050 btn-primary"
              onClick={handleSave}
              disabled={!dirty || saving}
            >
              {saving ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving
                </span>
              ) : (
                'Save changes'
              )}
            </button>
          </div>
        )}
      </div>
    </DataCard>
  );
}
